import { TCheckoutMgr } from 'widgets/checkout/CheckoutMgr';
import { TShippingMethodFormInstance } from 'core/checkout/CoreShippingMethodForm';
import { TCoreShippingFormInstance } from 'core/checkout/CoreShippingForm';
import { TPickupPointFormInstance } from './PickupPointForm';
import { get, getUrlParams } from 'widgets/toolbox/util';

import { TRefElementInstance } from 'widgets/toolbox/RefElement';
import { TOrderModel as TBaseOrderModel } from 'types/models/order';
import { TTabsInstance } from 'widgets/global/Tabs';
import { TCoreBillingFormInstance } from 'core/checkout/CoreBillingForm';

type TOrderModel = TBaseOrderModel & {
    totals: Record<string, unknown>;
    items?: { items: Array<object> } | Array<object>;
};

export type TPaymentMethod = {
    name: string;
    id: string;
};

/**
 * @param Widget mixin to handle focus
 * @returns Checkout Manager widget
 */

function CoreCheckoutMgrClassCreator(CheckoutMgr: TCheckoutMgr) {
    class CoreCheckoutMgr extends CheckoutMgr {
        currentStep = '';

        prevStep = '';

        isStepActivatedFromHistory = false;

        order?: TOrderModel;

        isPickupShipping = false;

        customer?: Record<string, unknown> & {
            profile: {
                firstName: string;
            };
        };

        get currentFormId() {
            return this.currentStep === 'shipping' ? this.prefs().shippingFormID : this.prefs().billingFormID;
        }

        prefs() {
            return {
                shippingMethodFormID: 'dwfrm_shippingMethod',
                pickupPointFormID: 'dwfrm_pickupPoint',
                ...super.prefs()
            };
        }

        /**
         * @description Initialize widget logic
         * * render shipping methods list
         * * activate current step
         * * init events
         * * init properties
         * @listens "checkout.paymentmethod.updated"
         * @listens "coupon.updated"
         * @listens "giftcertificate.updated"
         */
        init() {
            super.init();
            this.eventBus().on('coupon.updated', 'onCouponUpdated');
            this.eventBus().on('giftcertificate.updated', 'onGiftCertificateUpdated');
        }

        /**
         * @description Get initialization checkout step
         * @returns Returns shipping/billing/summary step name
         */
        getInitialStep(): 'shipping' | 'billing' | 'summary' {
            const urlParams = <{ step: string; stage: string }>getUrlParams();

            const isShippingCompleted = this.order?.steps?.shipping?.iscompleted;
            const isBillingCompleted = this.order?.steps?.billing?.iscompleted;

            let step;

            if (!isShippingCompleted || (!isBillingCompleted && urlParams.step !== 'billing')) {
                step = 'shipping';
            } else if (!isBillingCompleted) {
                step = 'billing';
            } else if (typeof urlParams.step === 'string') {
                step = urlParams.step;
            } else if (urlParams.stage === 'payment' || urlParams.stage === 'placeOrder') {
                // placeOrder mainly used in case of error from payment integration
                // in SFRA payment error was shown on review step, in Launch 360 on Billing step
                step = 'billing';
            } else {
                step = 'summary';
            }

            if (this.prefs().shippingSkipped && step === 'shipping') {
                step = 'billing';
            }

            if (step === 'summary') {
                this.callIfExists('sendSummaryEvents'); // in CoreSummaryStepMixin
            }

            return step;
        }

        /**
         * @description Handle click on step edit button
         * @param el - Edit button ref element
         * @listens dom#click
         */
        editStep(el: TRefElementInstance) {
            switch (el.data('step')) {
                case 'shipping':
                    this.activateStepByName('shipping');
                    // Handle focus logic CheckoutStepMixin - setFocusOnFirstInput
                    this.callIfExists('setFocusOnFirstInput', 'shipping');
                    break;
                case 'billing':
                    this.activateStepByName('billing');
                    // Handle focus logic CheckoutStepMixin - setFocusOnFirstInput
                    this.callIfExists('setFocusOnFirstInput', 'billing');
                    break;
                default:
                    break;
            }
        }

        /**
         * @description Post-Initialize widget logic
         * * render shipping methods list
         * * activate current step
         */
        postInit() {
            super.postInit();

            const basket = this.prefs().basket;

            this.isPickupShipping = !!get(this.order, 'shipping.0.isPickupPointShipment') && !!get(this.order, 'shipping.0.isPickupPoint');
            this.toggleExpressCheckout();

            this.getById(this.prefs().shippingMethodFormID, (shippingMethodFormComponent: TShippingMethodFormInstance) =>
                shippingMethodFormComponent.updateState(basket)
            );
        }

        /**
         * @description Executes on pcoupon update
         * @emits "order.updated"
         * @param order Order object
         */
        onCouponUpdated(order: TOrderModel) {
            if (order && order.couponAction === 'applied') {
                order.isShowAppliedMessage = true;
            } else if (order && order.couponAction === 'removed') {
                order.isCouponRemoved = true;
            }

            this.eventBus().emit('order.updated', order);
            this.callIfExists('updateSummaryEv', null, order);
        }

        /**
         * @description Executes on gift certificate update
         * @emits "order.updated"
         * @param order Order object
         */
        onGiftCertificateUpdated(order: TOrderModel) {
            if (order && order.gcAction === 'applied') {
                order.isShowAppliedGiftCertificateMessage = true;
                order.isShowGiftCertificateMessage = true;
            }

            if (order && order.gcAction === 'removed') {
                order.isShowRemovedGiftCertificateMessage = true;
                order.isShowGiftCertificateMessage = true;
            }

            this.eventBus().emit('order.updated', order);
            this.callIfExists('updateSummaryEv', null, order);
        }

        /**
         * @description Handle shipping type change
         * @param tabs Tabs instance
         * @listens "tabs.activePanelChanged"
         */
        handleShippingTypeChange(tabs: TTabsInstance) {
            this.isPickupShipping = tabs.activePanel === 'pickup';
            this.toggleExpressCheckout();

            this.getById(this.prefs().billingFormID, (billingForm: TCoreBillingFormInstance) => {
                if (billingForm) {
                    billingForm.toggleDeliveryType(this.isPickupShipping);
                }
            });

            if (this.isPickupShipping) {
                this.getByIdDeep(this.prefs().pickupPointFormID, (pickupFormComponent: TPickupPointFormInstance) => {
                    this.getById(this.prefs().shippingMethodFormID, (shippingMethodFormComponent: TShippingMethodFormInstance) => {
                        shippingMethodFormComponent.updateShippingState(pickupFormComponent);
                    });
                });
            } else {
                this.getByIdDeep(this.prefs().shippingFormID, (shippingFormComponent: TCoreShippingFormInstance) => {
                    this.getById(this.prefs().shippingMethodFormID, (shippingMethodFormComponent: TShippingMethodFormInstance) => {
                        shippingMethodFormComponent.updateShippingState(shippingFormComponent);
                    });
                });
            }
        }

        private toggleExpressCheckout() {
            this.ref('expressCheckout').toggle(!this.isPickupShipping);
        }

        /**
         * @description Activate next step
         *
         * Overrides CheckoutMgr nextStep method to focus the first
         * input of the next step instead of the Edit button
         */
        nextStep() {
            const nextStep = this.getNextStepName();

            this.activateStepByName(nextStep);

            this.callIfExists('setFocusOnFirstInput', nextStep);
        }
    }

    return CoreCheckoutMgr;
}

export type TCoreCheckoutMgr = ReturnType<typeof CoreCheckoutMgrClassCreator>;

export type TCoreCheckoutMgrInstance = InstanceType<TCoreCheckoutMgr>;

export default CoreCheckoutMgrClassCreator;
